export const RESERVED_CONTRACT_METADATA_FIELDS = new Set([
  "signerName",
  "acceptedAt",
]);

export type ContractMetadataField = {
  key: string;
  type: "string";
  required: boolean;
  label: string;
  description?: string;
  maxLength?: number;
  order?: number;
  reserved: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readableLabel(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

export function parseContractMetadataSchema(
  input: unknown,
): ContractMetadataField[] {
  let schema: unknown = input;
  if (typeof input === "string") {
    try {
      schema = JSON.parse(input);
    } catch {
      throw new Error("The agreement metadata configuration is invalid.");
    }
  }

  if (!isRecord(schema)) {
    throw new Error("The agreement metadata configuration is invalid.");
  }

  return Object.entries(schema)
    .map(([key, property]): ContractMetadataField => {
      if (!/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(key) || !isRecord(property)) {
        throw new Error("The agreement metadata configuration is invalid.");
      }
      if (property.type !== "string") {
        throw new Error(
          `The agreement field "${readableLabel(key)}" is not supported.`,
        );
      }
      if (
        property.required !== undefined &&
        typeof property.required !== "boolean"
      ) {
        throw new Error("The agreement metadata configuration is invalid.");
      }
      if (property.label !== undefined && typeof property.label !== "string") {
        throw new Error("The agreement metadata configuration is invalid.");
      }
      if (
        property.description !== undefined &&
        typeof property.description !== "string"
      ) {
        throw new Error("The agreement metadata configuration is invalid.");
      }
      if (
        property.maxLength !== undefined &&
        (typeof property.maxLength !== "number" ||
          !Number.isInteger(property.maxLength) ||
          property.maxLength < 1)
      ) {
        throw new Error("The agreement metadata configuration is invalid.");
      }
      if (
        property.order !== undefined &&
        (typeof property.order !== "number" || !Number.isFinite(property.order))
      ) {
        throw new Error("The agreement metadata configuration is invalid.");
      }

      return {
        key,
        type: "string",
        required: property.required === true,
        label:
          typeof property.label === "string" && property.label.trim()
            ? property.label.trim()
            : readableLabel(key),
        ...(typeof property.description === "string" &&
        property.description.trim()
          ? { description: property.description.trim() }
          : {}),
        ...(typeof property.maxLength === "number"
          ? { maxLength: property.maxLength }
          : {}),
        ...(typeof property.order === "number"
          ? { order: property.order }
          : {}),
        reserved: RESERVED_CONTRACT_METADATA_FIELDS.has(key),
      };
    })
    .sort(
      (left, right) =>
        (left.order ?? Number.MAX_SAFE_INTEGER) -
          (right.order ?? Number.MAX_SAFE_INTEGER) ||
        left.key.localeCompare(right.key),
    );
}

export function validateContractMetadataValues(
  fields: ContractMetadataField[],
  values: Record<string, string>,
): string | null {
  for (const field of fields) {
    if (field.reserved) continue;
    const value = values[field.key] ?? "";
    if (field.required && value.trim() === "") {
      return `${field.label} is required.`;
    }
    if (field.maxLength !== undefined && value.length > field.maxLength) {
      return `${field.label} must be ${field.maxLength} characters or fewer.`;
    }
  }
  return null;
}
